
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Form, Input, Button, Upload, Select, Card, Typography, Divider, Row, Col, message, Space } from "antd";
import { UserOutlined, MailOutlined, PhoneOutlined, GlobalOutlined, LockOutlined, PlusOutlined, EnvironmentOutlined, FileTextOutlined, TeamOutlined, HomeOutlined, ApartmentOutlined } from "@ant-design/icons";
import authApi from "../../api/authApi";
import api from "../../api/config";
import { prepareFormData, validateFileType, validateFileSize } from "../../utils/formDataHelper";


const AssociationRegister = ({ editingAssociation = null, isAdminEdit = false, onSuccess = null }) => {
  const [form] = Form.useForm();
  const [formData, setFormData] = useState({
    associationName: "",
    representativeName: "",
    designation: "",
    email: "",
    phone: "",
    website: "",
    industry: "",
    registrationNumber: "",
    yearOfEstablishment: "",
    address: "",
    district: "",
    description: "",
    password: "",
    password2: "",
    logo: null,
    registrationCertificate: null,
    latitude: 20.9374,
    longitude: 77.7796,
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const {
    associationName,
    representativeName,
    designation,
    email,
    phone,
    website,
    industry,
    registrationNumber,
    yearOfEstablishment,
    address,
    district,
    description,
    password,
    password2,
  } = formData;


  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const response = await fetch('/api/districts');
        const data = await response.json();
        if (data.success) {
          setDistricts(data.data || []);
        }
      } catch (error) {
        toast.error('Failed to load districts');
      }
    };
    fetchDistricts();
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (editingAssociation) {
      setFormData({
        associationName: editingAssociation.associationName || "",
        representativeName: editingAssociation.presidentName || editingAssociation.representativeName || "",
        designation: editingAssociation.designation || "",
        email: editingAssociation.email || "",
        phone: editingAssociation.phone || "",
        website: editingAssociation.website || "",
        industry: editingAssociation.industry || "",
        registrationNumber: editingAssociation.registrationNumber || "",
        yearOfEstablishment: editingAssociation.yearOfEstablishment || "",
        address: editingAssociation.address || "",
        district: editingAssociation.district || "",
        description: editingAssociation.description || "",
        password: "",
        password2: "",
        logo: null,
        registrationCertificate: null,
        latitude: editingAssociation.location?.coordinates?.[1] || 20.9374,
        longitude: editingAssociation.location?.coordinates?.[0] || 77.7796,
      });
      if (editingAssociation.logo) {
        const logoUrl = editingAssociation.logo.startsWith('http') 
          ? editingAssociation.logo 
          : `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${editingAssociation.logo.startsWith('/') ? '' : '/'}${editingAssociation.logo}`;
        setLogoPreview(logoUrl);
      }
    }
  }, [editingAssociation]);


  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'logo' && files && files[0]) {
      const file = files[0];
      setFormData(prev => ({ ...prev, logo: file }));
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleLocationSelect = ({ latitude, longitude }) => {
    setFormData(prev => ({ ...prev, latitude, longitude }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!associationName.trim()) newErrors.associationName = 'Association name is required';
    if (!representativeName.trim()) newErrors.representativeName = 'Representative name is required';
    if (!designation.trim()) newErrors.designation = 'Designation is required';
    if (!email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email is invalid';
    if (!phone) newErrors.phone = 'Phone number is required';
    else if (!/^[0-9]{10}$/.test(phone)) newErrors.phone = 'Phone number must be 10 digits';
    if (!website) newErrors.website = 'Website is required';
    else if (!/^https?:\/\//.test(website)) newErrors.website = 'Website must start with http:// or https://';
    if (!industry) newErrors.industry = 'Industry is required';
    // if (!registrationNumber) newErrors.registrationNumber = 'Registration number is required';
    if (!yearOfEstablishment) newErrors.yearOfEstablishment = 'Year of establishment is required';
    else if (yearOfEstablishment < 1900 || yearOfEstablishment > new Date().getFullYear()) {
      newErrors.yearOfEstablishment = 'Please enter a valid year';
    }
    if (!address) newErrors.address = 'Address is required';
    if (!district) newErrors.district = 'District is required';
    if (!description) newErrors.description = 'Description is required';
    
    // Skip password validation when editing
    if (!editingAssociation) {
      if (!password) newErrors.password = 'Password is required';
      else if (password.length < 6) newErrors.password = 'Password must be at least 6 characters';
      if (password !== password2) newErrors.password2 = 'Passwords do not match';
    }
    
    // File validations
    if (formData.logo) {
      if (!validateFileType(formData.logo, ['image/jpeg', 'image/png', 'image/gif'])) {
        newErrors.logo = 'Only JPG, PNG or GIF files are allowed';
      } else if (!validateFileSize(formData.logo, 5)) { // 5MB max
        newErrors.logo = 'Logo size should be less than 5MB';
      }
    } else if (!editingAssociation) {
      // Only require logo for new registrations
      newErrors.logo = 'Logo is required';
    }
    
    // if (formData.registrationCertificate) {
    //   if (!validateFileType(formData.registrationCertificate, ['application/pdf', 'image/jpeg', 'image/png'])) {
    //     newErrors.registrationCertificate = 'Only PDF, JPG or PNG files are allowed';
    //   } else if (!validateFileSize(formData.registrationCertificate, 10)) { // 10MB max
    //     newErrors.registrationCertificate = 'File size should be less than 10MB';
    //   }
    // } else {
    //   newErrors.registrationCertificate = 'Registration certificate is required';
    // }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fill in all required fields before submitting (see highlighted fields below).');
      return;
    }

    setLoading(true);
    
    try {
      // Map the form data to match backend model requirements
      const mappedFormData = {
        ...formData,
        presidentName: formData.representativeName, // Map representativeName to presidentName
        contactPerson: formData.representativeName, // Use same value for contactPerson
      };

      // Don't include password when editing
      if (editingAssociation) {
        delete mappedFormData.password;
        delete mappedFormData.password2;
      }
      
      // Prepare form data for file upload
      const formDataToSend = prepareFormData(mappedFormData);
      
      let responseData;
      
      if (editingAssociation) {
        // Update existing association
        // Note: Don't set Content-Type manually - axios will set it with proper boundary
        const response = await api.put(`/admin/associations/${editingAssociation._id}`, formDataToSend);
        responseData = response.data;
        toast.success("Association updated successfully!");
        
        // Call onSuccess callback if in admin edit mode
        if (onSuccess) {
          onSuccess();
        }
      } else {
        // Register new association
        await authApi.registerAssociation(formDataToSend);
        toast.success("Registration successful! Please log in.");
        
        // Clear form data after successful registration
        setFormData({
          associationName: "",
          representativeName: "",
          designation: "",
          email: "",
          phone: "",
          website: "",
          industry: "",
          registrationNumber: "",
          yearOfEstablishment: "",
          address: "",
          district: "",
          description: "",
          password: "",
          password2: "",
          logo: null,
          registrationCertificate: null,
          latitude: 20.9374,
          longitude: 77.7796,
        });
        setLogoPreview(null);
        setErrors({});
        
        // Only navigate if not in admin edit mode
        if (!isAdminEdit) {
          navigate("/login");
        }
      }
    } catch (error) {
      console.error("Registration error:", error);
      
      // Handle validation errors from the server
      if (error.response?.data?.errors) {
        const errorMsgs = error.response.data.errors.map(err => err.msg).join("\n");
        toast.error(errorMsgs);
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(error.message || "Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };


  // --- UI: Ant Design theme, matching ConsultantRegister ---
  const baseStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#333",
  };
  const inputStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "15px",
    borderRadius: "6px",
  };
  const labelStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "16px",
    fontWeight: 400,
    color: "#333",
    marginBottom: "6px",
  };

  // Ant Design Upload for logo
  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <div style={{ padding: isAdminEdit ? "0" : "20px", maxWidth: "800px", margin: "0 auto" }}>
      {!isAdminEdit && (
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <Typography.Title level={2} style={{ ...baseStyle, fontSize: "28px", fontWeight: 500, color: "#470172" }}>
            Association Registration
          </Typography.Title>
          <Typography.Text style={{ ...baseStyle, fontSize: "16px", opacity: 0.7 }}>
            Register your association for platform access
          </Typography.Text>
        </div>
      )}
      <Card bordered style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(71, 1, 114, 0.10)", marginBottom: 32 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark="optional"
          initialValues={formData}
        >
          {/* Logo Upload */}
          <Form.Item label={<span style={labelStyle}>Association Logo</span>} style={{ marginBottom: 20 }}>
            <Upload
              listType="picture-card"
              showUploadList={false}
              beforeUpload={() => false}
              accept="image/*"
              maxCount={1}
              onChange={e => handleChange({ target: { name: 'logo', files: e.fileList.map(f => f.originFileObj).filter(Boolean) } })}
            >
              {logoPreview ? (
                <img src={logoPreview} alt="logo" style={{ width: '100%', height: 120, objectFit: 'contain', borderRadius: 8 }} />
              ) : (
                uploadButton
              )}
            </Upload>
            <Typography.Text style={{ fontSize: 12, color: '#999' }}>
              Upload association logo (Max 5MB)
            </Typography.Text>
            {errors.logo && <div style={{ color: 'red', marginTop: 4 }}>{errors.logo}</div>}
          </Form.Item>

          <Divider style={{ margin: "24px 0" }} />
          <Typography.Title level={4} style={{ ...baseStyle, fontSize: 18, fontWeight: 500, marginBottom: 16, color: "#470172" }}>
            Association Details
          </Typography.Title>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span style={labelStyle}>Association Name</span>}
                required
                validateStatus={errors.associationName ? 'error' : ''}
                help={errors.associationName}
              >
                <Input
                  name="associationName"
                  value={formData.associationName}
                  onChange={handleChange}
                  prefix={<ApartmentOutlined style={{ color: "#470172" }} />}
                  placeholder="Enter association name"
                  style={inputStyle}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span style={labelStyle}>Representative Name</span>}
                required
                validateStatus={errors.representativeName ? 'error' : ''}
                help={errors.representativeName}
              >
                <Input
                  name="representativeName"
                  value={formData.representativeName}
                  onChange={handleChange}
                  prefix={<UserOutlined style={{ color: "#470172" }} />}
                  placeholder="Full name"
                  style={inputStyle}
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span style={labelStyle}>Designation</span>}
                required
                validateStatus={errors.designation ? 'error' : ''}
                help={errors.designation}
              >
                <Input
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  prefix={<FileTextOutlined style={{ color: "#470172" }} />}
                  placeholder="e.g., President, Secretary"
                  style={inputStyle}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span style={labelStyle}>Industry/Sector</span>}
                required
                validateStatus={errors.industry ? 'error' : ''}
                help={errors.industry}
              >
                <Input
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  prefix={<FileTextOutlined style={{ color: "#470172" }} />}
                  placeholder="Industry sector"
                  style={inputStyle}
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span style={labelStyle}>Year of Establishment</span>}
                required
                validateStatus={errors.yearOfEstablishment ? 'error' : ''}
                help={errors.yearOfEstablishment}
              >
                <Input
                  name="yearOfEstablishment"
                  type="number"
                  value={formData.yearOfEstablishment}
                  onChange={handleChange}
                  prefix={<FileTextOutlined style={{ color: "#470172" }} />}
                  placeholder="e.g., 2020"
                  style={inputStyle}
                  size="large"
                  min={1900}
                  max={new Date().getFullYear()}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span style={labelStyle}>Email</span>}
                required
                validateStatus={errors.email ? 'error' : ''}
                help={errors.email}
              >
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  prefix={<MailOutlined style={{ color: "#470172" }} />}
                  placeholder="association@example.com"
                  style={inputStyle}
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span style={labelStyle}>Phone/Mobile</span>}
                required
                validateStatus={errors.phone ? 'error' : ''}
                help={errors.phone}
              >
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  prefix={<PhoneOutlined style={{ color: "#470172" }} />}
                  placeholder="+1 (555) 000-0000"
                  style={inputStyle}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span style={labelStyle}>Website</span>}
                required
                validateStatus={errors.website ? 'error' : ''}
                help={errors.website}
              >
                <Input
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  prefix={<GlobalOutlined style={{ color: "#470172" }} />}
                  placeholder="https://example.com"
                  style={inputStyle}
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span style={labelStyle}>District</span>}
                required
                validateStatus={errors.district ? 'error' : ''}
                help={errors.district}
              >
                <Select
                  name="district"
                  value={formData.district || undefined}
                  onChange={value => handleChange({ target: { name: 'district', value } })}
                  placeholder="Select District"
                  style={inputStyle}
                  size="large"
                >
                  {districts.map((dist) => (
                    <Select.Option key={dist._id} value={dist.districtName}>
                      {dist.districtName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span style={labelStyle}>Address</span>}
                required
                validateStatus={errors.address ? 'error' : ''}
                help={errors.address}
              >
                <Input
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  prefix={<HomeOutlined style={{ color: "#470172" }} />}
                  placeholder="Complete address"
                  style={inputStyle}
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>
          <Divider style={{ margin: "24px 0" }} />
          <Typography.Title level={4} style={{ ...baseStyle, fontSize: 18, fontWeight: 500, marginBottom: 16, color: "#470172" }}>
            About Your Association
          </Typography.Title>
          <Form.Item
            label={<span style={labelStyle}>Description</span>}
            required
            validateStatus={errors.description ? 'error' : ''}
            help={errors.description}
          >
            <Input.TextArea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder="Describe your association's mission, activities, and membership..."
              style={inputStyle}
            />
          </Form.Item>
          {!editingAssociation && (
            <>
              <Divider style={{ margin: "24px 0" }} />
              <Typography.Title level={4} style={{ ...baseStyle, fontSize: 18, fontWeight: 500, marginBottom: 16, color: "#470172" }}>
                Account Security
              </Typography.Title>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span style={labelStyle}>Password</span>}
                    required
                  >
                    <Input.Password
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      prefix={<LockOutlined style={{ color: "#470172" }} />}
                      placeholder="Min. 6 characters"
                      style={inputStyle}
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span style={labelStyle}>Confirm Password</span>}
                    required
                  >
                    <Input.Password
                      name="password2"
                      value={formData.password2}
                      onChange={handleChange}
                      prefix={<LockOutlined style={{ color: "#470172" }} />}
                      placeholder="Re-enter password"
                      style={inputStyle}
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Typography.Text style={{ color: '#888', fontSize: 13 }}>
                Your information will be verified before account activation.
              </Typography.Text>
            </>
          )}
          <Form.Item style={{ marginTop: 32, textAlign: "center" }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              style={{
                fontFamily: "Rubik, sans-serif",
                fontSize: "16px",
                fontWeight: 500,
                height: "48px",
                width: "100%",
                maxWidth: "300px",
                backgroundColor: "#470172",
                borderColor: "#470172",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(71, 1, 114, 0.25)",
              }}
            >
              {loading ? (editingAssociation ? "Updating..." : "Registering...") : (editingAssociation ? "Update Association" : "Register Association")}
            </Button>
            {!isAdminEdit && (
              <div style={{ marginTop: 16 }}>
                <Typography.Text style={{ fontSize: 16, color: '#555' }}>
                  Already have an account?{' '}
                  <a href="/login" style={{ color: '#470172', fontWeight: 500 }}>Login here</a>
                </Typography.Text>
              </div>
            )}
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default AssociationRegister;
